import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import Layout from '../components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UserCircle, Plus, Upload, Trash2, BarChart, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const emptyAgentForm = () => ({
  agent_id: '', name: '', email: '', team_id: '', supervisor_name: '', status: 'active',
});

const AgentManagement = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  /* ── add ──────────────────────────────────────────────── */
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [agentForm, setAgentForm] = useState(emptyAgentForm());

  /* ── edit ─────────────────────────────────────────────── */
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [editForm, setEditForm] = useState(emptyAgentForm());

  /* ── delete confirm ───────────────────────────────────── */
  const [deleteTarget, setDeleteTarget] = useState(null);

  /* ── bulk upload ──────────────────────────────────────── */
  const [isBulkDialogOpen, setIsBulkDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [agentsRes, teamsRes] = await Promise.all([
        axios.get(`${API}/agents`),
        axios.get(`${API}/teams`),
      ]);
      setAgents(agentsRes.data);
      setTeams(teamsRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  /* ── ADD ──────────────────────────────────────────────── */
  const handleAddAgent = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/agents`, agentForm);
      toast.success('Agent added successfully!');
      setIsAddDialogOpen(false);
      setAgentForm(emptyAgentForm());
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add agent');
    }
  };

  /* ── EDIT – open ──────────────────────────────────────── */
  const openEdit = (agent) => {
    setEditingAgent(agent);
    setEditForm({
      agent_id: agent.agent_id,
      name: agent.name,
      email: agent.email,
      team_id: agent.team_id || '',
      supervisor_name: agent.supervisor_name || '',
      status: agent.status,
    });
    setIsEditOpen(true);
  };

  /* ── EDIT – submit ────────────────────────────────────── */
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/agents/${editingAgent.id}`, {
        name: editForm.name,
        email: editForm.email,
        team_id: editForm.team_id || null,
        supervisor_name: editForm.supervisor_name || null,
        status: editForm.status,
      });
      toast.success('Agent updated successfully!');
      setIsEditOpen(false);
      setEditingAgent(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update agent');
    }
  };

  /* ── DELETE (deactivate) ──────────────────────────────── */
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/agents/${deleteTarget.id}`);
      toast.success('Agent deactivated!');
      setDeleteTarget(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to deactivate agent');
    }
  };

  /* ── BULK UPLOAD ──────────────────────────────────────── */
  const handleBulkUpload = async () => {
    if (!selectedFile) { toast.error('Please select a CSV file'); return; }
    try {
      const fd = new FormData();
      fd.append('file', selectedFile);
      const res = await axios.post(`${API}/agents/bulk-upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadResult(res.data);
      toast.success(`Uploaded ${res.data.success_count} agents successfully!`);
      if (res.data.failed_count > 0) toast.error(`${res.data.failed_count} agents failed`);
      fetchData();
    } catch (err) {
      toast.error('Bulk upload failed');
    }
  };

  const downloadTemplate = () => {
    const csv = 'agent_id,name,email,team_id,supervisor_name,status\nAGT001,John Doe,john@example.com,,Jane Manager,active';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'agent_upload_template.csv'; a.click();
  };

  const getTeamName = (teamId) => teams.find((t) => t.id === teamId)?.name || 'No Team';

  /* ─── Agent form fields (shared by add + edit) ────────── */
  const AgentFormFields = ({ form, setForm, readOnlyId = false }) => (
    <>
      <div>
        <Label htmlFor="agent-id">Agent ID *</Label>
        <Input id="agent-id" placeholder="e.g., AGT001" value={form.agent_id} readOnly={readOnlyId}
          className={readOnlyId ? 'bg-slate-50 cursor-not-allowed' : ''}
          onChange={(e) => !readOnlyId && setForm({ ...form, agent_id: e.target.value })}
          required data-testid="agent-id-input" />
        {readOnlyId && <p className="text-xs text-slate-400 mt-0.5">Agent ID cannot be changed</p>}
      </div>
      <div>
        <Label htmlFor="agent-name">Name *</Label>
        <Input id="agent-name" placeholder="Full name" value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          required data-testid="agent-name-input" />
      </div>
      <div>
        <Label htmlFor="agent-email">Email *</Label>
        <Input id="agent-email" type="email" placeholder="email@example.com" value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          required data-testid="agent-email-input" />
      </div>
      <div>
        <Label htmlFor="agent-team">Team (Optional)</Label>
        <Select value={form.team_id || 'none'}
          onValueChange={(v) => setForm({ ...form, team_id: v === 'none' ? '' : v })}>
          <SelectTrigger id="agent-team" data-testid="agent-team-select">
            <SelectValue placeholder="Select team" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No Team</SelectItem>
            {teams.map((team) => (
              <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="supervisor-name">Supervisor Name</Label>
        <Input id="supervisor-name" placeholder="Supervisor's name" value={form.supervisor_name}
          onChange={(e) => setForm({ ...form, supervisor_name: e.target.value })}
          data-testid="supervisor-name-input" />
      </div>
      <div>
        <Label htmlFor="agent-status">Status</Label>
        <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
          <SelectTrigger id="agent-status" data-testid="agent-status-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </>
  );

  /* ─────────────────────────────────────────────────────────
     RENDER
  ────────────────────────────────────────────────────────── */
  return (
    <Layout>
      <div className="space-y-8 fade-in" data-testid="agent-management-page">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Agent Management
            </h1>
            <p className="text-slate-600">Manage call center agents and track their performance</p>
          </div>
          <div className="flex gap-3">

            {/* ── Bulk Upload ──────────────────────────────────── */}
            <Dialog open={isBulkDialogOpen} onOpenChange={setIsBulkDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-slate-300" data-testid="bulk-upload-button">
                  <Upload size={20} className="mr-2" />Bulk Upload
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Bulk Upload Agents</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm font-medium text-slate-900 mb-2">CSV Format Required:</p>
                    <code className="text-xs text-slate-700 block mb-2">
                      agent_id,name,email,team_id,supervisor_name,status
                    </code>
                    <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="download-template-button">
                      Download Template
                    </Button>
                  </div>
                  <div>
                    <Label htmlFor="csv-file">Select CSV File</Label>
                    <Input id="csv-file" type="file" accept=".csv"
                      onChange={(e) => setSelectedFile(e.target.files[0])}
                      data-testid="csv-file-input" className="mt-1" />
                  </div>
                  {uploadResult && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded flex-1">
                          <p className="text-sm font-medium text-emerald-900">Success: {uploadResult.success_count}</p>
                        </div>
                        <div className="p-3 bg-rose-50 border border-rose-200 rounded flex-1">
                          <p className="text-sm font-medium text-rose-900">Failed: {uploadResult.failed_count}</p>
                        </div>
                      </div>
                      {uploadResult.failed_agents?.length > 0 && (
                        <div className="max-h-40 overflow-y-auto">
                          {uploadResult.failed_agents.map((f, i) => (
                            <div key={i} className="text-xs text-rose-600 p-2 border-b border-rose-100">{f.error}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <Button onClick={handleBulkUpload} className="w-full" disabled={!selectedFile}
                    data-testid="upload-csv-button">
                    Upload CSV
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* ── Add Agent ──────────────────────────────────────── */}
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#4338CA] hover:bg-[#3730A3] text-white" data-testid="add-agent-button">
                  <Plus size={20} className="mr-2" />Add Agent
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add New Agent</DialogTitle></DialogHeader>
                <form onSubmit={handleAddAgent} className="space-y-4" data-testid="add-agent-form">
                  <AgentFormFields form={agentForm} setForm={setAgentForm} readOnlyId={false} />
                  <Button type="submit" className="w-full" data-testid="submit-agent-button">Add Agent</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* ── Agents Grid ──────────────────────────────────────── */}
        {loading ? (
          <p className="text-slate-600">Loading agents...</p>
        ) : agents.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {agents.map((agent) => (
              <Card key={agent.id} className="p-6 border border-slate-200 shadow-sm card-hover"
                data-testid={`agent-card-${agent.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-indigo-50 rounded-lg">
                      <UserCircle size={24} className="text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900">{agent.name}</h3>
                      <p className="text-xs text-slate-600">{agent.agent_id}</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                      onClick={() => navigate(`/agent-performance/${agent.id}`)}
                      data-testid={`view-performance-${agent.id}`} title="View performance">
                      <BarChart size={15} className="text-slate-500" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                      onClick={() => openEdit(agent)}
                      data-testid={`edit-agent-${agent.id}`} title="Edit agent">
                      <Pencil size={15} className="text-indigo-500" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                      onClick={() => setDeleteTarget(agent)}
                      data-testid={`delete-agent-${agent.id}`} title="Deactivate agent">
                      <Trash2 size={15} className="text-rose-500" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div><p className="text-xs text-slate-600">Email</p><p className="text-slate-900">{agent.email}</p></div>
                  <div><p className="text-xs text-slate-600">Team</p><p className="text-slate-900">{getTeamName(agent.team_id)}</p></div>
                  {agent.supervisor_name && (
                    <div><p className="text-xs text-slate-600">Supervisor</p><p className="text-slate-900">{agent.supervisor_name}</p></div>
                  )}
                  <div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${
                      agent.status === 'active'
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : 'bg-slate-100 text-slate-700 border-slate-200'
                    }`}>
                      {agent.status}
                    </span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-12 text-center border border-slate-200 shadow-sm">
            <UserCircle size={48} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-600 mb-4">No agents added yet</p>
            <div className="flex gap-3 justify-center">
              <Button onClick={() => setIsAddDialogOpen(true)} data-testid="add-first-agent-button">Add Agent</Button>
              <Button variant="outline" onClick={() => setIsBulkDialogOpen(true)} data-testid="bulk-upload-first-button">Bulk Upload</Button>
            </div>
          </Card>
        )}

        {/* ── EDIT dialog ──────────────────────────────────────── */}
        <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Edit Agent</DialogTitle></DialogHeader>
            {editingAgent && (
              <form onSubmit={handleEditSubmit} className="space-y-4" data-testid="edit-agent-form">
                <AgentFormFields form={editForm} setForm={setEditForm} readOnlyId={true} />
                <div className="flex gap-3 pt-2">
                  <Button type="submit" className="flex-1 bg-[#4338CA] hover:bg-[#3730A3] text-white"
                    data-testid="save-agent-button">
                    Save Changes
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* ── DELETE confirm ────────────────────────────────────── */}
        <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Deactivate Agent?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to deactivate <strong>{deleteTarget?.name}</strong>?
                Their call history will be preserved but they will be marked as inactive.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-rose-600 hover:bg-rose-700 text-white"
                onClick={handleDelete}
                data-testid="confirm-deactivate-agent">
                Deactivate
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

      </div>
    </Layout>
  );
};

export default AgentManagement;
